import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Grid,
  Skeleton,
  Button,
  ButtonBase,
} from "@mui/material";
import BrokenImageIcon from "@mui/icons-material/BrokenImage";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import EditIcon from "@mui/icons-material/Edit";
import PromotionHero from "./PromotionHero";
import axios from "axios";
import { getAuthCredentials } from "../../utils/auth";
import UploadLogo from "./Functions/uploadLogo";
import UploadBanner from "./Functions/uploadBanner";

const StatusDot = ({ active = false }) => (
  <Stack direction="row" alignItems="center" spacing={0.5}>
    <FiberManualRecordIcon
      fontSize="small"
      sx={{ color: active ? "success.main" : "error.main" }}
    />
    <Typography variant="caption" color="text.secondary">
      {active ? "Active" : "Inactive"}
    </Typography>
  </Stack>
);

const PromoBrands = () => {
  const [loading, setLoading] = useState(true);
  const [logo, setLogo] = useState(null);
  const [banner, setBanner] = useState(null);
  const [logoOpen, setLogoOpen] = useState(false);
  const [bannerOpen, setBannerOpen] = useState(false);

  const fetchBrands = useCallback(async () => {
    try {
      setLoading(true);
      const { token } = await getAuthCredentials();
      const [logoRes, bannerRes] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/promotion/fetch-logo`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/promotion/fetch-banner`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
      ]);
      setLogo(logoRes.data?.logo || null);
      setBanner(bannerRes.data?.banner || null);
    } catch {
      setLogo(null);
      setBanner(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  return (
    <Box sx={{ p: 3 }}>
      <PromotionHero title="Promotions — Brandings" />

      <Paper elevation={3} sx={{ mt: -2, borderRadius: 3, p: 3 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          Brand Assets
        </Typography>

        {loading ? (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Skeleton variant="rounded" height={220} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Skeleton variant="rounded" height={220} />
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={1}
                >
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Logo
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <StatusDot active={!!logo?.isActive} />
                      <Typography variant="caption" color="text.secondary">
                        {logo?.title || "—"}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => setLogoOpen(true)}
                    disabled={loading}
                  >
                    {logo ? "Edit Logo" : "Add Logo"}
                  </Button>
                </Stack>

                <Box sx={{ mt: 2 }}>
                  {logo?.url ? (
                    <ButtonBase
                      onClick={() => setLogoOpen(true)}
                      sx={{
                        width: "100%",
                        borderRadius: 2,
                        overflow: "hidden",
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Box
                        component="img"
                        src={logo.url}
                        alt={logo.alt || "logo"}
                        sx={{
                          width: "100%",
                          height: 220,
                          objectFit: "contain",
                          background: "#fff",
                        }}
                      />
                    </ButtonBase>
                  ) : (
                    <Stack
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        height: 220,
                        borderRadius: 2,
                        border: "1px dashed",
                        borderColor: "divider",
                        color: "text.secondary",
                      }}
                    >
                      <BrokenImageIcon />
                      <Typography variant="caption">Nothing found</Typography>
                    </Stack>
                  )}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={1}
                >
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Banner
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <StatusDot active={!!banner?.isActive} />
                      <Typography variant="caption" color="text.secondary">
                        {banner?.title || "—"}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => setBannerOpen(true)}
                    disabled={loading}
                  >
                    {banner ? "Edit Banner" : "Add Banner"}
                  </Button>
                </Stack>

                <Box sx={{ mt: 2 }}>
                  {banner?.url ? (
                    <ButtonBase
                      onClick={() => setBannerOpen(true)}
                      sx={{
                        width: "100%",
                        borderRadius: 2,
                        overflow: "hidden",
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Box
                        component="img"
                        src={banner.url}
                        alt={banner.alt || "banner"}
                        sx={{
                          width: "100%",
                          height: 220,
                          objectFit: "contain",
                          background: "#fff",
                        }}
                      />
                    </ButtonBase>
                  ) : (
                    <Stack
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        height: 220,
                        borderRadius: 2,
                        border: "1px dashed",
                        borderColor: "divider",
                        color: "text.secondary",
                      }}
                    >
                      <BrokenImageIcon />
                      <Typography variant="caption">Nothing found</Typography>
                    </Stack>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Paper>

      <UploadLogo
        key={`logo-${logo?.url || "none"}`}
        open={logoOpen}
        onClose={() => setLogoOpen(false)}
        onSuccess={fetchBrands}
        existingLogo={logo}
      />
      <UploadBanner
        key={`banner-${banner?.url || "none"}`}
        open={bannerOpen}
        onClose={() => setBannerOpen(false)}
        onSuccess={fetchBrands}
        existingBanner={banner}
      />
    </Box>
  );
};

export default PromoBrands;
